import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/lib/auth';
import { cn } from '@/lib/cn';

export default function Login() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(username, password);
        toast.success('Acceso concedido');
      } else {
        await register(username, password);
        toast.success('Usuario registrado');
      }
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex w-20 h-20 items-center justify-center bg-primary border-2 border-black shadow-[5px_5px_0_0_#000000] mb-5">
            <span
              className="material-symbols-outlined text-5xl text-black"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              restaurant_menu
            </span>
          </div>
          <h1 className="font-condensed text-6xl uppercase tracking-tight text-primary leading-none">
            Empanada
          </h1>
          <h1 className="font-condensed text-6xl uppercase tracking-tight text-text-bright leading-none">
            BI
          </h1>
          <p className="text-secondary mt-3 font-mono text-xs uppercase tracking-widest">
            // Kinetic::v1.0 :: Acceso Seguro
          </p>
        </div>

        <div className="bg-surface-container border-2 border-outline p-6 shadow-[6px_6px_0_0_#000000]">
          <div className="flex mb-6 border-2 border-outline">
            <button
              onClick={() => setMode('login')}
              className={cn(
                'flex-1 py-3 font-bold uppercase tracking-widest text-xs transition-colors duration-150',
                mode === 'login'
                  ? 'bg-primary text-black'
                  : 'bg-surface text-text-muted hover:text-text-bright cursor-pointer',
              )}
            >
              Iniciar Sesión
            </button>
            <button
              onClick={() => setMode('register')}
              className={cn(
                'flex-1 py-3 font-bold uppercase tracking-widest text-xs transition-colors duration-150 border-l-2 border-outline',
                mode === 'register'
                  ? 'bg-primary text-black'
                  : 'bg-surface text-text-muted hover:text-text-bright cursor-pointer',
              )}
            >
              Registrar
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                // USERNAME
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className="kinetic-input"
                placeholder="jose.gonzalez"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                // PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'register' ? 8 : 6}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className="kinetic-input"
                placeholder="••••••••"
              />
              {mode === 'register' && (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                  Mínimo 8 caracteres
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="kinetic-button w-full mt-2"
            >
              {loading && (
                <span className="material-symbols-outlined animate-spin">progress_activity</span>
              )}
              {mode === 'login' ? '→ Entrar' : '→ Crear cuenta'}
            </button>
          </form>

          {mode === 'register' && (
            <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
              // Solo funciona si la BD está vacía
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
